import type { ClientMeasurementRow } from "./types.js";

/**
 * All available client measurements with their metadata
 */
export const clientMeasurementRows: ClientMeasurementRow[] = [
  {
    id: "backNeckToWaist",
    ref: "A-B",
    measurement: "Back neck to waist",
    howToMeasure:
      "Measure from the bone at the nape of the neck to the waistline.",
  },
  {
    id: "shirtLength",
    ref: "A-C",
    measurement: "Shirt length",
    howToMeasure:
      "Measure from the bone at the nape of the neck to the required shirt or T-shirt hem.",
  },
  {
    id: "jacketLength",
    ref: "A-C",
    measurement: "Jacket length",
    howToMeasure:
      "Measure from the bone at the nape of the neck to the required jacket hem, usually covering the seat.",
  },
  {
    id: "coatLength",
    ref: "A-C",
    measurement: "Coat length",
    howToMeasure:
      "Measure from the bone at the nape of the neck to the required coat hem, usually near the knee. Overcoats, trench coats, frock coats and capes read this length.",
  },
  {
    id: "halfBack",
    ref: "D-E",
    measurement: "Half across the back",
    howToMeasure:
      "Measure from the centre back, 15 cm below the nape, to the sleeve seam at the back scye. For accuracy, measure the full back width and divide by two.",
  },
  {
    id: "chest",
    ref: "F",
    measurement: "Chest",
    howToMeasure:
      "Measure around the body at chest level, under the arms. Ensure the tape passes over the shoulder blades and the body is relaxed.",
  },
  {
    id: "naturalWaist",
    ref: "G",
    measurement: "Natural waist",
    howToMeasure:
      "Measure around the natural waist at the waist indent. Keep the body relaxed.",
  },
  {
    id: "trouserWaist",
    ref: "H",
    measurement: "Trouser waist position",
    howToMeasure:
      "Measure around the body approximately 5 cm below the natural waist, where the trousers will sit.",
  },
  {
    id: "seat",
    ref: "I",
    measurement: "Seat",
    howToMeasure:
      "Measure around the fullest part of the seat, usually approximately 21 cm below the waistline.",
  },
  {
    id: "neck",
    ref: "J",
    measurement: "Neck",
    howToMeasure:
      "Measure comfortably around the base of the neck. Read the measurement at the lower edge of the tape.",
  },
  {
    id: "shoulder",
    ref: "K-L",
    measurement: "Shoulder",
    howToMeasure: "Measure from the base of the neck to the shoulder bone.",
  },
  {
    id: "onePieceSleeveLength",
    ref: "L-M",
    measurement: "One-piece sleeve length",
    howToMeasure:
      "Measure from the shoulder bone to just below the wrist bone.",
  },
  {
    id: "shortOnePieceSleeveLength",
    ref: "L-M short",
    measurement: "Short one-piece sleeve length",
    howToMeasure:
      "Measure from the shoulder bone to the required short sleeve hem.",
  },
  {
    id: "twoPieceSleeveLength",
    ref: "D-N",
    measurement: "Two-piece sleeve length",
    howToMeasure:
      "With the arm raised and bent, measure from the centre back, 15 cm below the neck bone, across the back and through the elbow to the wrist bone.",
  },
  {
    id: "elbowLength",
    ref: "D-O",
    measurement: "Elbow length",
    howToMeasure:
      "Measure from the centre back to the elbow point. Particularly useful for unusually long or short arms.",
  },
  {
    id: "shirtSleeveLength",
    ref: "A-L-N",
    measurement: "Shirt sleeve length",
    howToMeasure:
      "With the arm raised and bent, measure from the neck bone, across the shoulder, through the elbow, to just below the wrist bone.",
  },
  {
    id: "trouserSideSeam",
    ref: "P-Q",
    measurement: "Trouser side-seam length",
    howToMeasure:
      "Measure from the trouser waist position to the heel seam of the shoe.",
  },
  {
    id: "bodyRise",
    ref: "R-S",
    measurement: "Body rise",
    howToMeasure:
      "Sit upright on a flat stool. Measure vertically from the trouser waist position to the top of the stool.",
  },
  {
    id: "insideLeg",
    ref: "T-U",
    measurement: "Inside leg",
    howToMeasure:
      "Measure from high in the crotch to the heel seam of the shoe.",
  },
  {
    id: "trouserBottomWidth",
    ref: "-",
    measurement: "Trouser bottom width",
    howToMeasure:
      "Record the required finished width at the bottom of the trouser leg.",
  },
  {
    id: "wrist",
    ref: "V",
    measurement: "Wrist circumference",
    howToMeasure: "Measure around the wrist immediately above the wrist bone.",
  },
  {
    id: "armCircumference",
    ref: "W",
    measurement: "Arm circumference",
    howToMeasure:
      "Bend the arm and measure around the fullest part of the upper arm.",
    group: "extra",
  },
  {
    id: "scyeDepth",
    ref: "A-X",
    measurement: "Scye depth",
    howToMeasure:
      "Place the tape horizontally across the back under the arms. Measure vertically from the neck bone to the centre of the tape.",
    group: "extra",
  },
  // Women's wear, numbered as in Aldrich, Metric Pattern Cutting (4th ed.),
  // pp. 170–171. Women's drafts also read waist, hips (seat), back width
  // (halfBack), shoulder, neck, top arm, wrist, nape to waist, armscye depth,
  // body rise and sleeve length from the rows above.
  {
    id: "bust",
    ref: "W1",
    measurement: "Bust",
    howToMeasure:
      "Measure round the fullest point of the bust, keeping the tape level at the back. The bust sets the drafting size.",
    group: "womens",
  },
  {
    id: "frontChestWidth",
    ref: "W5",
    measurement: "Front chest width",
    howToMeasure:
      "Measure across the front from armscye to armscye, 7 cm down from the neck point at the centre front. A width, not the chest girth.",
    group: "womens",
  },
  {
    id: "bustDart",
    ref: "W8",
    measurement: "Bust dart",
    howToMeasure:
      "Not measured: take the standard dart for the bust size from the size chart (7 cm at size 12).",
    group: "womens",
  },
  {
    id: "frontShoulderToWaist",
    ref: "W14",
    measurement: "Front shoulder to waist",
    howToMeasure:
      "Measure from the centre of the front shoulder, over the bust point, to a string tied round the waist.",
    group: "womens",
  },
  {
    id: "waistToHip",
    ref: "W17",
    measurement: "Waist to hip",
    howToMeasure:
      "Standard measurement from the size chart: the depth from the waist to the hip line (20.6 cm at size 12).",
    group: "womens",
  },
  {
    id: "waistToKnee",
    ref: "-",
    measurement: "Waist to knee",
    howToMeasure:
      "Measure from the string at the waist down to the knee. Taken from the size chart when not measured.",
    group: "womens",
  },
  {
    id: "waistToFloor",
    ref: "W18",
    measurement: "Waist to floor",
    howToMeasure:
      "Measure from the waist to the floor at the centre back. Measure the front too to check the figure's balance.",
    group: "womens",
  },
  {
    id: "skirtLength",
    ref: "W16",
    measurement: "Skirt length",
    howToMeasure:
      "Measure from the string at the waist down to the required hem length.",
    group: "womens",
  },
  {
    id: "ankle",
    ref: "W11",
    measurement: "Ankle",
    howToMeasure: "Measure around the ankle, over the ankle bone.",
    group: "womens",
  },
  {
    id: "highAnkle",
    ref: "W12",
    measurement: "High ankle",
    howToMeasure: "Measure around the leg just above the ankle.",
    group: "womens",
  },
  {
    id: "jerseySleeveLength",
    ref: "-",
    measurement: "Jersey sleeve length",
    howToMeasure:
      "Standard measurement from the size chart for jersey and flat-cut sleeves, about 6 cm shorter than the sleeve length.",
    group: "womens",
  },
  // Children's wear, lettered as in Aldrich, Metric Pattern Cutting for
  // Children's Wear (4th ed.), pp. 31–33. Children's drafts also read chest,
  // waist, seat, across back (halfBack), neck, shoulder, upper arm, wrist,
  // scye depth, neck to waist, waist to hip, waist to knee, body rise, inside
  // leg, arm length (onePieceSleeveLength) and ankle from the rows above.
  {
    id: "height",
    ref: "C-A",
    measurement: "Height",
    howToMeasure:
      "Standing erect, feet together and barefoot, from the crown of the head to the soles of the feet. Sets a child's size; scye depth and waist to hip are taken from the size chart for it.",
    group: "extra",
  },
  {
    id: "cervicalHeight",
    ref: "C-K-O",
    measurement: "Cervical height",
    howToMeasure: "Measure from the cervical (the neck bone at the nape) down to the soles of the feet.",
    group: "childrens",
  },
  {
    id: "headCircumference",
    ref: "C-U",
    measurement: "Head circumference",
    howToMeasure: "Measure the horizontal girth of the head. Used for hoods.",
    group: "childrens",
  },
  {
    id: "verticalTrunk",
    ref: "C-V",
    measurement: "Vertical trunk",
    howToMeasure:
      "Measure from the centre of one shoulder, down the back, under the crotch and up over the abdomen and chest back to the same shoulder. Used for sleepsuits and rompers.",
    group: "childrens",
  },
  {
    id: "footLength",
    ref: "C-X-Y",
    measurement: "Foot length",
    howToMeasure: "Measure the foot from the heel to the top of the big toe.",
    group: "childrens",
  },
];
